import { __assign } from 'tslib';
import { Injectable, defineInjectable, inject, Component, ElementRef, Renderer2, Input, NgModule } from '@angular/core';
import { DashboardWidgetService, FunnelChartComponent, ParliamentChartComponent, PieChartComponent, TimelineComponent, DashboardWidgetsModule } from 'dashboard-widgets';
import { SidenavService, DialogService, SerendipityComponentsModule } from 'serendipity-components';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { LoggerService, AngularMaterialModule, UtilsModule, ConsoleLoggerService } from 'utils';
import * as screenfull from 'screenfull';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { GridsterModule } from 'angular-gridster2';
import { DynamicModule } from 'ng-dynamic-component';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @enum {string} */
var DisplayGrid = {
    Always: 'always',
    OnDragAndResize: 'onDrag&Resize',
    None: 'none',
};
/** @enum {string} */
var CompactType = {
    None: 'none',
    CompactUp: 'compactUp',
    CompactLeft: 'compactLeft',
    CompactUpAndLeft: 'compactUp&Left',
    CompactLeftAndUp: 'compactLeft&Up',
    CompactRight: 'compactRight',
    CompactUpAndRight: 'compactUp&Right',
    CompactRightAndUp: 'compactRight&Up',
};
/** @enum {string} */
var GridType = {
    Fit: 'fit',
    ScrollVertical: 'scrollVertical',
    ScrollHorizontal: 'scrollHorizontal',
    Fixed: 'fixed',
    VerticalFixed: 'verticalFixed',
    HorizontalFixed: 'horizontalFixed',
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var MockDashboardService = /** @class */ (function () {
    function MockDashboardService(httpClient) {
        this.httpClient = httpClient;
        this.DASHBOARDS = 'assets/data/dashboards.json';
        this.TOOL_PALETTE_ITEMS = 'assets/data/tool-palette-items.json';
    }
    /**
     * @return {?}
     */
    MockDashboardService.prototype.getDashboards = /**
     * @return {?}
     */
    function () {
        return this.httpClient.get(this.DASHBOARDS);
    };
    /**
     * @param {?} dashboardId
     * @return {?}
     */
    MockDashboardService.prototype.getDashboard = /**
     * @param {?} dashboardId
     * @return {?}
     */
    function (dashboardId) {
        return this.httpClient.get(this.DASHBOARDS).pipe(map((/**
         * @param {?} dashboards
         * @return {?}
         */
        function (dashboards) {
            return dashboards.find((/**
             * @param {?} dashboard
             * @return {?}
             */
            function (dashboard) { return dashboard.id === dashboardId; }));
        })));
    };
    /**
     * @return {?}
     */
    MockDashboardService.prototype.getToolPaletteItems = /**
     * @return {?}
     */
    function () {
        return this.httpClient.get(this.TOOL_PALETTE_ITEMS);
    };
    /**
     * @param {?} widgetId
     * @return {?}
     */
    MockDashboardService.prototype.getToolPaletteItem = /**
     * @param {?} widgetId
     * @return {?}
     */
    function (widgetId) {
        return this.httpClient.get(this.TOOL_PALETTE_ITEMS).pipe(map((/**
         * @param {?} toolPaletteItems
         * @return {?}
         */
        function (toolPaletteItems) {
            return toolPaletteItems.find((/**
             * @param {?} toolPaletteItem
             * @return {?}
             */
            function (toolPaletteItem) { return toolPaletteItem.id === widgetId; }));
        })));
    };
    MockDashboardService.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    /** @nocollapse */
    MockDashboardService.ctorParameters = function () { return [
        { type: HttpClient }
    ]; };
    /** @nocollapse */ MockDashboardService.ngInjectableDef = defineInjectable({ factory: function MockDashboardService_Factory() { return new MockDashboardService(inject(HttpClient)); }, token: MockDashboardService, providedIn: "root" });
    return MockDashboardService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var DashboardComponent = /** @class */ (function () {
    function DashboardComponent(elementRef, renderer, commandBarSidenavService, dashboardService, dashboardWidgetService, dialogService, logger) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.commandBarSidenavService = commandBarSidenavService;
        this.dashboardService = dashboardService;
        this.dashboardWidgetService = dashboardWidgetService;
        this.dialogService = dialogService;
        this.logger = logger;
        this.screenFull = (/** @type {?} */ (screenfull));
        this.canDrop = true;
        this.components = {
            funnelChart: FunnelChartComponent,
            parliamentChart: ParliamentChartComponent,
            pieChart: PieChartComponent,
            timeline: TimelineComponent
        };
    }
    /**
     * @return {?}
     */
    DashboardComponent.prototype.ngOnInit = /**
     * @return {?}
     */
    function () {
        this.logger.info('DashboardComponent: ngOnInit()');
        this.getOptions();
        this.getToolPaletteItems();
        this.subscribe();
    };
    /**
     * @return {?}
     */
    DashboardComponent.prototype.getOptions = /**
     * @return {?}
     */
    function () {
        //
        // There is some documentation re angular-gridster2's config properties in this file: gridsterConfig.constant.ts
        // See: https://github.com/tiberiuzuld/angular-gridster2/blob/master/projects/angular-gridster2/src/lib/gridsterConfig.constant.ts
        //
        this.options = {
            disablePushOnDrag: true,
            displayGrid: DisplayGrid.Always,
            draggable: {
                enabled: true,
                ignoreContent: true,
                // dropOverItems: true,
                dropOverItems: false,
                dragHandleClass: 'drag-handler',
                ignoreContentClass: 'no-drag',
            },
            emptyCellDragMaxCols: 50,
            emptyCellDragMaxRows: 50,
            emptyCellDropCallback: this.onDrop.bind(this),
            enableEmptyCellClick: false,
            enableEmptyCellContextMenu: false,
            enableEmptyCellDrop: true,
            enableEmptyCellDrag: false,
            gridType: GridType.Fit,
            itemResizeCallback: this.itemResize.bind(this),
            // maxCols: 6,
            // maxRows: 6,
            minCols: 10,
            // 6
            minRows: 10,
            // 6
            pushDirections: { north: true, east: true, south: true, west: true },
            pushItems: true,
            resizable: { enabled: true }
            // swap: true,
        };
    };
    /**
     * @protected
     * @return {?}
     */
    DashboardComponent.prototype.subscribe = /**
     * @protected
     * @return {?}
     */
    function () {
        var _this = this;
        this.logger.info('DashboardComponent: subscribe()');
        this.subscription = this.dashboardService.getDashboard(this.dashboardId).subscribe((/**
         * @param {?} data
         * @return {?}
         */
        function (data) {
            _this.items = data.widgets;
            _this.logger.info('Dashboard Id: ' + JSON.stringify(data.id));
            _this.logger.info('Widgets: ' + JSON.stringify(_this.items));
        }));
    };
    /**
     * @return {?}
     */
    DashboardComponent.prototype.getToolPaletteItems = /**
     * @return {?}
     */
    function () {
        var _this = this;
        /** @type {?} */
        var subscription = this.dashboardService.getToolPaletteItems().subscribe((/**
         * @param {?} data
         * @return {?}
         */
        function (data) {
            _this.toolPaletteItems = data;
            _this.logger.info('toolPaletteItems: ' + JSON.stringify(_this.toolPaletteItems));
            subscription.unsubscribe();
        }));
    };
    /**
     * @param {?} widgetId
     * @return {?}
     */
    DashboardComponent.prototype.getToolPaletteItem = /**
     * @param {?} widgetId
     * @return {?}
     */
    function (widgetId) {
        return this.toolPaletteItems.find((/**
         * @param {?} toolPaletteItem
         * @return {?}
         */
        function (toolPaletteItem) { return toolPaletteItem.id === widgetId; }));
    };
    /**
     * @protected
     * @return {?}
     */
    DashboardComponent.prototype.unsubscribe = /**
     * @protected
     * @return {?}
     */
    function () {
        this.logger.info('DashboardComponent: unsubscribe()');
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    };
    /**
     * @param {?} changes
     * @return {?}
     */
    DashboardComponent.prototype.ngOnChanges = /**
     * @param {?} changes
     * @return {?}
     */
    function (changes) {
        this.logger.info('DashboardComponent: ngOnChanges()');
        this.dashboardId = changes.dashboardId.currentValue;
        this.unsubscribe();
        this.subscribe();
    };
    /**
     * @param {?} event
     * @return {?}
     */
    DashboardComponent.prototype.onDragEnter = /**
     * @param {?} event
     * @return {?}
     */
    function (event) {
        this.logger.info('DashboardComponent: onDragEnter()');
        //
        // Deleting a widget (GridsterItem) leaves a gridster-preview behind
        // See: https://github.com/tiberiuzuld/angular-gridster2/issues/516
        //
        /** @type {?} */
        var gridsterPreviewElements = this.elementRef.nativeElement.getElementsByTagName('gridster-preview');
        // this.renderer.setStyle(gridsterPreview[0], 'display', 'block');
        this.renderer.setStyle(gridsterPreviewElements[0], 'background', 'rgba(0, 0, 0, .15)');
    };
    /**
     * @param {?} event
     * @return {?}
     */
    DashboardComponent.prototype.onDrop = /**
     * @param {?} event
     * @return {?}
     */
    function (event) {
        var _this = this;
        this.logger.info('DashboardComponent: onDrop()');
        //
        // emptyCellDropCallback is called twice
        // See: https://github.com/tiberiuzuld/angular-gridster2/issues/513
        //
        this.logger.info('DashboardComponent: canDrop === ' + this.canDrop);
        if (this.canDrop) {
            this.canDrop = false;
            /** @type {?} */
            var widgetId = event.dataTransfer.getData('widgetIdentifier');
            /** @type {?} */
            var toolPaletteItem = this.getToolPaletteItem(widgetId);
            /** @type {?} */
            var widget = __assign({ cols: 4, rows: 4, y: 0, x: 0 }, toolPaletteItem);
            this.items.push(widget);
            setTimeout((/**
             * @return {?}
             */
            function () {
                _this.canDrop = true;
            }), 1000);
        }
        // this.logger.info('Widget Id: ' + widgetId);
        // this.logger.info('toolPaletteItem: ' + JSON.stringify(toolPaletteItem));
        // this.logger.info('widget: ' + JSON.stringify(widget));
    };
    /**
     * @param {?} item
     * @param {?} itemComponent
     * @return {?}
     */
    DashboardComponent.prototype.itemResize = /**
     * @param {?} item
     * @param {?} itemComponent
     * @return {?}
     */
    function (item, itemComponent) {
        this.logger.info('DashboardComponent: itemResize()');
        this.dashboardWidgetService.reflowWidgets();
    };
    /**
     * @return {?}
     */
    DashboardComponent.prototype.itemChange = /**
     * @return {?}
     */
    function () {
        this.logger.info('DashboardComponent: itemChange()');
    };
    /**
     * @return {?}
     */
    DashboardComponent.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        this.logger.info('DashboardComponent: ngOnDestroy()');
        if (this.commandBarSidenavService.isOpen()) {
            this.logger.info('commandBarSidenav is open');
            this.commandBarSidenavService.toggle();
        }
        this.unsubscribe();
    };
    //
    // Toolbar events
    //
    //
    // Toolbar events
    //
    /**
     * @param {?} item
     * @return {?}
     */
    DashboardComponent.prototype.onDelete = 
    //
    // Toolbar events
    //
    /**
     * @param {?} item
     * @return {?}
     */
    function (item) {
        this.logger.info('DashboardComponent: onDelete()');
        this.items.splice(this.items.indexOf(item), 1);
        //
        // Deleting a widget (GridsterItem) leaves a gridster-preview behind
        // See: https://github.com/tiberiuzuld/angular-gridster2/issues/516
        //
        /** @type {?} */
        var gridsterPreviewElements = this.elementRef.nativeElement.getElementsByTagName('gridster-preview');
        // this.renderer.setStyle(gridsterPreview[0], 'display', 'none !important');
        this.renderer.setStyle(gridsterPreviewElements[0], 'background', '#fafafa');
        this.logger.info('Widgets: ' + JSON.stringify(this.items));
    };
    /**
     * @param {?} item
     * @return {?}
     */
    DashboardComponent.prototype.onSettings = /**
     * @param {?} item
     * @return {?}
     */
    function (item) {
        this.logger.info('DashboardComponent: onSettings()');
        this.dialogService.openAlert({
            title: 'Alert',
            message: 'You clicked the Settings button.',
            closeButton: 'CLOSE'
        });
    };
    DashboardComponent.decorators = [
        { type: Component, args: [{
                    // tslint:disable-next-line:component-selector
                    selector: 'dashboard',
                    template: "<gridster\n  [options]=\"options\"\n  (dragenter)=\"onDragEnter($event)\"\n  (drop)=\"onDrop($event)\"\n  style=\"background-color: transparent;\">\n\n  <ng-container *ngFor=\"let item of items\" style=\"overflow: hidden;\">\n\n    <gridster-item [item]=\"item\" class=\"crm-dashboard-widget\">\n\n      <mat-toolbar class=\"crm-dashboard-widget-toolbar\">\n        <mat-toolbar-row class=\"crm-dashboard-widget-toolbar-row\">\n\n          {{item.name}}\n\n          <div fxFlex fxLayout fxLayoutAlign=\"flex-end\">\n\n            <button mat-button class=\"crm-dashboard-widget-toolbar-button\">\n              <mat-icon class=\"drag-handler\">open_with</mat-icon>\n            </button>\n\n            <button mat-button class=\"crm-dashboard-widget-toolbar-button\" (click)=\"onSettings(item)\">\n              <mat-icon>settings</mat-icon>\n            </button>\n\n            <button mat-button class=\"crm-dashboard-widget-toolbar-button\" (click)=\"onDelete(item)\">\n              <mat-icon>delete</mat-icon>\n            </button>\n\n          </div>\n\n        </mat-toolbar-row>\n      </mat-toolbar>\n\n      <ndc-dynamic [ndcDynamicComponent]=components[item.component]></ndc-dynamic>\n\n    </gridster-item>\n\n  </ng-container>\n\n</gridster>\n",
                    styles: [".crm-dashboard-widget{border:1px solid #708090}.crm-dashboard-widget-toolbar{height:40px;min-height:40px;border-bottom:1px solid #708090}.crm-dashboard-widget-toolbar-row{height:40px;min-height:40px}@media (max-width:599px){.crm-dashboard-widget-toolbar,.crm-dashboard-widget-toolbar-row{height:40px;min-height:40px}}.crm-dashboard-widget-toolbar-button{min-width:32px;padding:0 4px}"]
                }] }
    ];
    /** @nocollapse */
    DashboardComponent.ctorParameters = function () { return [
        { type: ElementRef },
        { type: Renderer2 },
        { type: SidenavService },
        { type: MockDashboardService },
        { type: DashboardWidgetService },
        { type: DialogService },
        { type: LoggerService }
    ]; };
    DashboardComponent.propDecorators = {
        dashboardId: [{ type: Input }]
    };
    return DashboardComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
var DashboardModule = /** @class */ (function () {
    function DashboardModule(logger) {
        this.logger = logger;
        this.logger.info('Dashboard Module initialised');
    }
    DashboardModule.decorators = [
        { type: NgModule, args: [{
                    imports: [
                        AngularMaterialModule,
                        CommonModule,
                        // DragDropModule,
                        DashboardWidgetsModule,
                        DynamicModule.withComponents([
                            FunnelChartComponent,
                            ParliamentChartComponent,
                            PieChartComponent,
                            TimelineComponent
                        ]),
                        FlexLayoutModule,
                        GridsterModule,
                        SerendipityComponentsModule,
                        UtilsModule
                    ],
                    declarations: [DashboardComponent],
                    providers: [
                        { provide: LoggerService, useClass: ConsoleLoggerService }
                    ],
                    exports: [DashboardComponent],
                    entryComponents: [
                        FunnelChartComponent,
                        ParliamentChartComponent,
                        PieChartComponent,
                        TimelineComponent
                    ]
                },] }
    ];
    /** @nocollapse */
    DashboardModule.ctorParameters = function () { return [
        { type: LoggerService }
    ]; };
    return DashboardModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { CompactType, DashboardComponent, DashboardModule, DisplayGrid, GridType, MockDashboardService };
//# sourceMappingURL=dashboard.js.map
